//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

/* eslint-disable react-hooks/exhaustive-deps */
import { Transition } from 'history';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBlocker } from './useBlock';

type PendingAction = (() => void) | null;
export function useCallbackPrompt(when: boolean): [boolean, () => void, () => void, (action: () => void) => void] {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [lastLocation, setLastLocation] = useState<Transition>();
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const pendingActionRef = useRef<PendingAction>(null);
  const bypassBlockRef = useRef(false);
  const cancelNavigation = useCallback(() => {
    setShowPrompt(false);
    setConfirmedNavigation(false);
    pendingActionRef.current = null;
  }, []);

  const confirmNavigation = useCallback(() => {
    setShowPrompt(false);
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      bypassBlockRef.current = true;
      // 讓 bypassBlockRef.current 只在本次確認後生效；有些 action 不會觸發導航，因此攔截器不會清除這個 flag。
      try {
        action();
      } finally {
        bypassBlockRef.current = false;
      }
      return;
    }
    setConfirmedNavigation(true);
  }, []);

  const requestConfirm = useCallback((action: () => void) => {
    pendingActionRef.current = action;
    setShowPrompt(true);
  }, []);

  const handleBlockedNavigation = useCallback(
    (nextLocation: Transition) => {
      // in if condition we are checking next location and current location are equals or not
      if (bypassBlockRef.current) {
        bypassBlockRef.current = false;
        /**
         * 呼叫 retry() 以繼續先前被攔下的 navigation；否則目標 location 不會生效。
         * 沒有呼叫 retry() 時，按 Yes 後 tab 內容會切換，但 URL 不會更新。
         */
        nextLocation.retry();
        return true;
      }
      if (!confirmedNavigation && nextLocation.location.pathname !== location.pathname) {
        setShowPrompt(true);
        setLastLocation(nextLocation);
        return false;
      }
      return true;
    },
    [confirmedNavigation, location.pathname]
  );

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      navigate(`${lastLocation.location.pathname}${lastLocation.location.search}`);
      setConfirmedNavigation(false);
    }
  }, [confirmedNavigation, lastLocation]);

  useBlocker(handleBlockedNavigation, when);

  return [showPrompt, confirmNavigation, cancelNavigation, requestConfirm];
}
